import React, { useEffect, useState } from 'react';
import { Sparkles, Loader2, AlertCircle, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface InsightArticle {
  id: string;
  title: string;
  content: string;
  category: 'chakras' | 'healing' | 'angel-numbers' | 'spirituality';
  image_url: string;
  created_at: string;
}

const DEFAULT_INSIGHTS = [
  {
    title: "Understanding Your Chakras",
    content: "The seven main chakras are the body's key energy centers. From the Root to the Crown chakra, each one influences different aspects of your physical and spiritual well-being. Regular meditation and mindful practices can help keep these energy centers balanced and flowing freely.",
    category: "chakras",
    image_url: "https://images.unsplash.com/photo-1528319725582-ddc096101511?auto=format&fit=crop&q=80&w=1000",
  },
  {
    title: "Natural Healing Wisdom",
    content: "Holistic healing encompasses the mind, body, and spirit. Ancient practices like aromatherapy, crystal healing, and herbal remedies work alongside modern wellness techniques to promote natural healing and balance in our lives.",
    category: "healing",
    image_url: "https://images.unsplash.com/photo-1600334089648-b0d9d3028eb2?auto=format&fit=crop&q=80&w=1000",
  },
  {
    title: "The Magic of Angel Numbers",
    content: "Angel numbers are sequences that carry divine guidance. When you repeatedly see certain number patterns, it's often a message from your guardian angels. Each sequence has its own spiritual significance and can offer guidance for your life path.",
    category: "angel-numbers",
    image_url: "https://images.unsplash.com/photo-1516534775068-ba3e7458af70?auto=format&fit=crop&q=80&w=1000",
  },
  {
    title: "Daily Spiritual Practice",
    content: "Developing a daily spiritual practice can transform your life. Whether through meditation, prayer, or mindful moments in nature, connecting with your spiritual self helps maintain inner peace and clarity in our busy world.",
    category: "spirituality",
    image_url: "https://images.unsplash.com/photo-1507692049790-de58290a4334?auto=format&fit=crop&q=80&w=1000",
  },
];

export default function DailySpiritualInsights() {
  const [insights, setInsights] = useState<InsightArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInsight, setSelectedInsight] = useState<InsightArticle | null>(null);

  useEffect(() => {
    fetchDailyInsights();
  }, []);

  const fetchDailyInsights = async () => {
    try {
      setLoading(true);
      // Query database directly
      const { data: dbInsights, error: dbError } = await supabase
          .from('spiritual_insights')
          .select('*')
          .eq('date', new Date().toISOString().split('T')[0])
          .order('created_at', { ascending: false })
          .limit(4);

      if (dbError) {
        console.error('Error fetching insights:', dbError);
        throw dbError;
      }

      // If no insights found for today, use defaults
      if (!dbInsights || dbInsights.length === 0) {
        const defaultInsights = DEFAULT_INSIGHTS.map((insight, index) => ({
          id: `default-${Date.now()}-${index}`,
          ...insight,
          date: new Date().toISOString().split('T')[0],
          created_at: new Date().toISOString(),
        }));
        setInsights(defaultInsights);
      } else {
        setInsights(dbInsights);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching insights:', err);
      // Use default insights as fallback
      const defaultInsights = DEFAULT_INSIGHTS.map((insight, index) => ({
        id: `default-${Date.now()}-${index}`,
        ...insight,
        date: new Date().toISOString().split('T')[0],
        created_at: new Date().toISOString(),
      }));
      setInsights(defaultInsights);
      setError('Using default insights while we resolve connectivity issues.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <section className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-purple-600 mr-3" />
            Daily Spiritual Insights
          </h2>
          <p className="mt-4 text-xl text-gray-600">
            Discover daily wisdom for your spiritual journey
          </p>
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-50 rounded-lg flex items-center text-red-700">
            <AlertCircle className="w-5 h-5 mr-2" />
            <p>{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {insights.map((insight) => (
            <article
              key={insight.id}
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
            >
              <div className="relative h-48">
                <img
                  src={insight.image_url}
                  alt={insight.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="px-3 py-1 bg-purple-600 text-white text-sm rounded-full">
                    {insight.category.replace('-', ' ')}
                  </span>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {insight.title}
                </h3>
                <p className="text-gray-600 line-clamp-3">
                  {insight.content}
                </p>
                <button 
                  onClick={() => setSelectedInsight(insight)}
                  className="mt-4 text-purple-600 font-medium hover:text-purple-700 flex items-center"
                >
                  Read more
                  <svg className="w-4 h-4 ml-2" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M5 12h14M12 5l7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* Modal */}
        {selectedInsight && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="relative">
                <img
                  src={selectedInsight.image_url}
                  alt={selectedInsight.title}
                  className="w-full h-64 object-cover"
                />
                <button
                  onClick={() => setSelectedInsight(null)}
                  className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-8">
                <span className="px-3 py-1 bg-purple-600 text-white text-sm rounded-full">
                  {selectedInsight.category.replace('-', ' ')}
                </span>
                <h3 className="text-2xl font-bold mt-4 mb-6">
                  {selectedInsight.title}
                </h3>
                <div className="prose max-w-none">
                  <p className="text-gray-700 leading-relaxed">
                    {selectedInsight.content}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}